import useScrollIntoView from '@/hooks/useScrollIntoView';
import { DropdownActionBarProps } from '@/storybook/components/FormCollection/components/DropdownActionBar/DropdownActionBar';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { useFloatingDropdownPanel } from '@/storybook/components/FormCollection/components/DropdownList/useFloatingDropdownPanel';
import VirtualizedRows from '@/storybook/components/FormCollection/components/VirtualizedRows/VirtualizedRows';
import { useCollapsibleCategories } from '@/storybook/components/FormCollection/hooks/useCollapsibleCategories';
import {
  DropdownPosition,
  createStableObjectHash,
  handleDropdownListPosition,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { buildAvailableValueSet, formatActiveTotalBadge, getActiveTotalCount } from '@/utils/filter/cascadingFilters';
import cx from 'classnames';
import { JSX, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MultiSelectFloatingLabel.module.css';
import { useMultiSelectHandlersFloatingLabel } from './MultiSelectFloatingLabel/hooks/useMultiSelectHandlersFloatingLabel';
import { useMultiSelectOptionsFloatingLabel } from './MultiSelectFloatingLabel/hooks/useMultiSelectOptionsFloatingLabel';
import { useMultiSelectVirtualizationFloatingLabel } from './MultiSelectFloatingLabel/hooks/useMultiSelectVirtualizationFloatingLabel';

export type MultiSelectFloatingLabelProps<T = string> = {
  'label'?: ReactNode;
  'tooltip'?: ReactNode;
  'options'?: SelectItem<T>[];
  'unfilteredOptions'?: SelectItem<T>[];
  'searchable'?: boolean;
  'isLoading'?: boolean;
  'countLoading'?: boolean;
  'value'?: Array<T>;
  'onChange': (selectedValue?: Array<T>) => void;
  'onClear'?: VoidFunction;
  'placeholder'?: string | JSX.Element;
  'disabled'?: boolean;
  'clearable'?: boolean;
  'adobeEventTrackFunctions'?: VoidFunction;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'customSelectItemFn'?: (value: T, options: SelectItem<T>[]) => SelectItem<T> | undefined;
  'size'?: keyof typeof DropdownButtonSize;
  'className'?: string;
  'state'?: 'default' | 'filled' | 'error';
  'actionBarProps'?: DropdownActionBarProps;
  'collapsibleCategories'?: boolean;
  'defaultCategoriesCollapsed'?: boolean;
};

const getValueKey = (val: unknown) =>
  typeof val === 'object' && val !== null ? createStableObjectHash(val) : String(val);

export default function MultiSelectFloatingLabel<T>({
  label,
  tooltip,
  options,
  unfilteredOptions,
  isLoading = false,
  countLoading = false,
  value = [],
  onChange,
  onClear,
  placeholder,
  disabled = false,
  clearable = true,
  searchable = false,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'multiSelectFloatingLabelDropdown',
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  customSelectItemFn,
  size = 'L',
  className,
  state = 'default',
  actionBarProps,
  collapsibleCategories = false,
  defaultCategoriesCollapsed = false,
}: MultiSelectFloatingLabelProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const [searchInput, setSearchInput] = useState<string | undefined>();
  const [memoizedValueList, setMemoizedValueList] = useState<Array<T>>([]);
  const { t } = useTranslation();

  const { isCollapsibleActive, isCategoryCollapsed, toggleCategory, resetCategories } = useCollapsibleCategories({
    isEnabled: collapsibleCategories,
    isDefaultCollapsed: defaultCategoriesCollapsed,
    isSearching: !!searchInput,
  });

  const { dropdownRef, panelRef } = useFloatingDropdownPanel({ isOpen, onClose: () => setIsOpen(false) });
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  useScrollIntoView({ elementRef: dropdownRef, isVisible: isOpen });

  const { allOptions, isSelected, searchResults, selectedOptions, notSelectedOptions, categoriesToDisplay } =
    useMultiSelectOptionsFloatingLabel({
      options,
      unfilteredOptions,
      value,
      memoizedValueList,
      searchInput,
    });

  const { handleClearValue, handleOptionClick } = useMultiSelectHandlersFloatingLabel({
    value,
    onChange,
    onClear,
    disabled,
    clearable,
    isSelected,
  });

  const { virtualizedList, parentRef, renderVirtualRow } = useMultiSelectVirtualizationFloatingLabel({
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
    noCategoryLabel: t('components.dropdownPanel.noCategory'),
    handleOptionClick,
    isSelected,
    collapsibleCategories: isCollapsibleActive,
    isCategoryCollapsed,
    onToggleCategory: toggleCategory,
    // Raw prop, unlike isCollapsibleActive, so rows keep their inset while searching.
    insetOptionRows: collapsibleCategories,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => {
      const newState = !prev;
      if (newState) {
        handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
        setSearchInput(undefined);
        setMemoizedValueList(value);
        resetCategories();
      }
      return newState;
    });
  }, [disabled, dropdownPosition, dropdownRef, value, resetCategories]);

  useEffect(() => {
    onDropdownToggle?.(isOpen);
  }, [isOpen, onDropdownToggle]);

  const dropdownButtonText = useMemo(() => {
    if (!value.length) return '';
    return value
      .map(selectedValue => {
        if (customSelectItemFn) return customSelectItemFn(selectedValue, allOptions)?.label;
        return allOptions.find(opt => getValueKey(opt.value) === getValueKey(selectedValue))?.label;
      })
      .filter(Boolean)
      .join(', ');
  }, [value, allOptions, customSelectItemFn]);

  // Cascading filters: when options carry disabled flags, show `{active}/{total}`.
  const countLabel = useMemo(() => {
    const hasCascadingState = options?.some(option => option.disabled != null) ?? false;
    if (!hasCascadingState) return undefined;
    const availableValues = buildAvailableValueSet(options?.filter(option => option.disabled !== true));
    return formatActiveTotalBadge(getActiveTotalCount(value, availableValues)) ?? undefined;
  }, [options, value]);

  const displayLabel = label || placeholder;
  const isLabelFloating = value.length > 0 || isOpen;

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.floatingLabelContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
      data-state={state}
    >
      <section className={styles.dropdownContainer}>
        {displayLabel && (
          <label className={styles.floatingLabel} data-floating={isLabelFloating} data-has-value={value.length > 0}>
            {displayLabel}
          </label>
        )}
        <div className={styles.buttonWrapper}>
          <DropdownButton
            ref={dropdownButtonRef}
            type="multiSelect"
            size={size}
            placeholder=""
            numOfValue={value.length}
            active={isOpen}
            onClick={!disabled ? handleToggleDropdown : undefined}
            onClear={handleClearValue}
            clearable={clearable}
            disabled={disabled}
            countLabel={countLabel}
            countLoading={countLoading}
            className={styles.dropdownButton}
            data-testid={dropdownButtonTestId}
          >
            <div className={styles.value}>
              {dropdownButtonText ? (
                <span className={styles.valueText}>{dropdownButtonText}</span>
              ) : isLabelFloating && placeholder ? (
                <span className={cx(styles.valueText, styles.placeholder)}>{placeholder}</span>
              ) : null}
            </div>
          </DropdownButton>
        </div>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          isSearchable={searchable}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchResults.length}
          data-testid={dropdownListTestId}
          listRef={parentRef}
          dropdownTriggerRef={dropdownButtonRef}
          isFloating
          panelRef={panelRef}
          optionsCount={options?.length ?? 0}
          actionBarProps={actionBarProps}
        >
          <VirtualizedRows virtualization={virtualizedList} renderRow={renderVirtualRow} />
        </DropdownList>
      </section>
      {tooltip && <div className={styles.tooltip}>{tooltip}</div>}
    </div>
  );
}
